import React from 'react';
import type { SubjectDto, UserDto, TimetableDto, CreateTimetablePayload } from '../../api/axios';
import styles from './TimetableGrid.module.css';

const DAYS_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

/* ===== Create Modal ===== */

interface CreateModalProps {
  slot: { day: string; period: number } | null;
  data: Partial<CreateTimetablePayload>;
  subjects: SubjectDto[];
  teachers: UserDto[];
  onDataChange: (data: Partial<CreateTimetablePayload>) => void;
  onSave: () => void;
  onClose: () => void;
}

export const CreateModal: React.FC<CreateModalProps> = ({
  slot,
  data,
  subjects,
  teachers,
  onDataChange,
  onSave,
  onClose,
}) => {
  if (!slot) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHandle} />
        <div className={styles.modalTitle}>Add Class</div>
        <div className={styles.modalSubtitle}>
          {DAYS_LABELS[slot.day] || slot.day} &middot; Period {slot.period}
        </div>

        <div className={styles.modalField}>
          <label>Subject *</label>
          <select
            value={data.subject_id ?? ''}
            onChange={e => onDataChange({ ...data, subject_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select Subject</option>
            {subjects.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {subjects.length === 0 && (
            <p style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
              No linked subjects for this class. Add subjects and teacher links under Class–Teacher linking first.
            </p>
          )}
        </div>

        <div className={styles.modalField}>
          <label>Teacher *</label>
          <select
            value={data.teacher_id ?? ''}
            onChange={e => onDataChange({ ...data, teacher_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select Teacher</option>
            {teachers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.modalField}>
          <label>Room</label>
          <input
            type="text"
            value={data.room ?? ''}
            onChange={e => onDataChange({ ...data, room: e.target.value })}
            placeholder="e.g. Rm 7, Lab 1, Hall"
          />
        </div>

        <div className={styles.modalActions}>
          <button className={`${styles.modalBtn} ${styles.cancel}`} onClick={onClose}>
            Cancel
          </button>
          <button className={`${styles.modalBtn} ${styles.save}`} onClick={onSave}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== Edit Modal ===== */

interface EditModalProps {
  entry: TimetableDto;
  draft: { subject_id: number | null; teacher_id: number | null; room: string };
  subjects: SubjectDto[];
  teachers: UserDto[];
  onDraftChange: (draft: { subject_id: number | null; teacher_id: number | null; room: string }) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export const EditModal: React.FC<EditModalProps> = ({
  entry,
  draft,
  subjects,
  teachers,
  onDraftChange,
  onSave,
  onDelete,
  onClose,
}) => {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHandle} />
        <div className={styles.modalTitle}>Edit Class</div>
        <div className={styles.modalSubtitle}>
          {DAYS_LABELS[entry.day] || entry.day} &middot; Period {entry.period}
        </div>

        <div className={styles.modalField}>
          <label>Subject</label>
          <select
            value={draft.subject_id ?? ''}
            onChange={e => onDraftChange({ ...draft, subject_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select Subject</option>
            {subjects.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.modalField}>
          <label>Teacher</label>
          <select
            value={draft.teacher_id ?? ''}
            onChange={e => onDraftChange({ ...draft, teacher_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select Teacher</option>
            {teachers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.modalField}>
          <label>Room</label>
          <input
            type="text"
            value={draft.room}
            onChange={e => onDraftChange({ ...draft, room: e.target.value })}
            placeholder="e.g. Rm 7, Lab 1, Hall"
          />
        </div>

        <div className={styles.modalActions}>
          <button className={`${styles.modalBtn} ${styles.delete}`} onClick={onDelete}>
            Delete
          </button>
          <button className={`${styles.modalBtn} ${styles.cancel}`} onClick={onClose}>
            Cancel
          </button>
          <button className={`${styles.modalBtn} ${styles.save}`} onClick={onSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
